import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Wine, Instagram, Facebook } from 'lucide-react';
import { COMPANY_FOOTER_LINE } from '../lib/company';

const Footer = () => {
  return (
    <footer className="bg-[#1F3D2B] text-white" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-16">

          {/* Brand & About */}
          <div className="md:col-span-1">
            <h2
              className="text-2xl font-bold mb-4"
              style={{ fontFamily: 'Playfair Display, serif' }}
              data-testid="footer-logo"
            >
              VinePass
            </h2>
            <p className="text-white/70 text-sm leading-relaxed mb-6">
              VinePass is a curated ticketing platform focused on premium wine farm experiences
              across South Africa. We connect event organisers with audiences looking for
              unforgettable moments in some of the country's most beautiful locations.
            </p>
            <div className="flex items-center text-white/70 text-sm">
              <MapPin className="w-4 h-4 mr-2 shrink-0" />
              <span>Stellenbosch, Western Cape, South Africa</span>
            </div>

            {/* Follow VinePass */}
            <div className="mt-8" data-testid="footer-social">
              <h3 className="text-sm tracking-[0.15em] uppercase font-semibold text-white/50 mb-4">
                Follow VinePass
              </h3>
              <div className="flex items-center gap-3">
                <a
                  href="https://www.instagram.com/vinepass.co.za?igsh=ODY1ZnQ0M25mYms2&utm_source=qr"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow us on Instagram"
                  title="Follow us on Instagram"
                  className="group flex items-center justify-center w-10 h-10 rounded-full border border-white/15 bg-white/[0.03] text-white/70 hover:text-[#C9A27A] hover:border-[#C9A27A]/60 hover:bg-[#C9A27A]/10 transition-colors"
                  data-testid="footer-instagram-link"
                >
                  <Instagram className="w-4 h-4" />
                  <span className="sr-only">Follow us on Instagram</span>
                </a>
                <a
                  href="https://www.facebook.com/share/18unkAYnwV/?mibextid=wwXIfr"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow us on Facebook"
                  title="Follow us on Facebook"
                  className="group flex items-center justify-center w-10 h-10 rounded-full border border-white/15 bg-white/[0.03] text-white/70 hover:text-[#C9A27A] hover:border-[#C9A27A]/60 hover:bg-[#C9A27A]/10 transition-colors"
                  data-testid="footer-facebook-link"
                >
                  <Facebook className="w-4 h-4" />
                  <span className="sr-only">Follow us on Facebook</span>
                </a>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-sm tracking-[0.15em] uppercase font-semibold text-white/50 mb-5">
              Quick Links
            </h3>
            <ul className="space-y-3">
              <li>
                <Link to="/events" className="text-white/70 hover:text-white transition-colors text-sm" data-testid="footer-events-link">
                  Browse Events
                </Link>
              </li>
              <li>
                <Link to="/register?role=organiser" className="text-white/70 hover:text-white transition-colors text-sm" data-testid="footer-list-event-link">
                  List Your Event
                </Link>
              </li>
              <li>
                <Link to="/login" className="text-white/70 hover:text-white transition-colors text-sm" data-testid="footer-login-link">
                  Sign In
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-white/70 hover:text-white transition-colors text-sm" data-testid="footer-terms-link">
                  Terms & Conditions
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="text-white/70 hover:text-white transition-colors text-sm" data-testid="footer-privacy-link">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/refund-policy" className="text-white/70 hover:text-white transition-colors text-sm" data-testid="footer-refund-link">
                  Refund Policy
                </Link>
              </li>
              <li>
                <Link to="/organiser-terms" className="text-white/70 hover:text-white transition-colors text-sm" data-testid="footer-organiser-terms-link">
                  Organiser Terms
                </Link>
              </li>
              <li>
                <Link to="/payout-policy" className="text-white/70 hover:text-white transition-colors text-sm" data-testid="footer-payout-policy-link">
                  Payout Policy
                </Link>
              </li>
              <li>
                <Link to="/cookies" className="text-white/70 hover:text-white transition-colors text-sm" data-testid="footer-cookie-policy-link">
                  Cookie Policy
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-white/70 hover:text-white transition-colors text-sm" data-testid="footer-about-link">
                  About
                </Link>
              </li>
              <li>
                <Link to="/fees" className="text-white/70 hover:text-white transition-colors text-sm" data-testid="footer-fees-link">
                  VinePass Fees
                </Link>
              </li>
              <li>
                <Link to="/faq" className="text-white/70 hover:text-white transition-colors text-sm" data-testid="footer-faq-link">
                  FAQ
                </Link>
              </li>
            </ul>
          </div>

          {/* VinePass Fees */}
          <div>
            <h3 className="text-sm tracking-[0.15em] uppercase font-semibold text-white/50 mb-5">
              VinePass Fees
            </h3>
            <p className="text-white/70 text-sm leading-relaxed mb-4">
              VinePass applies a small service fee to each ticket to support secure payments,
              seamless ticket delivery, and platform operations.
            </p>
            <p className="text-white/60 text-xs leading-relaxed mb-4">
              For most events, this is a fixed fee per ticket. Event organisers may apply their
              own refund policies. Booking / curation fees are non-refundable. Approved refunds
              are processed within 7–10 business days.
            </p>
            <Link
              to="/fees"
              className="inline-flex items-center text-sm text-[#C9A27A] hover:text-white transition-colors group"
              data-testid="footer-fees-full-link"
            >
              View full pricing &amp; fees
              <span className="ml-1.5 transition-transform group-hover:translate-x-0.5" aria-hidden>→</span>
            </Link>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-sm tracking-[0.15em] uppercase font-semibold text-white/50 mb-5">
              Get In Touch
            </h3>
            <a
              href="mailto:info@vinepass.co.za"
              className="flex items-center text-white/70 hover:text-white transition-colors text-sm mb-2"
              data-testid="footer-email-link"
            >
              <Mail className="w-4 h-4 mr-2 shrink-0" />
              info@vinepass.co.za
            </a>
            <a
              href="mailto:tickets@vinepass.co.za"
              className="flex items-center text-white/70 hover:text-white transition-colors text-sm mb-4"
              data-testid="footer-ticket-support-link"
            >
              <Mail className="w-4 h-4 mr-2 shrink-0" />
              tickets@vinepass.co.za
            </a>
            <div className="mt-6">
              <h3 className="text-sm tracking-[0.15em] uppercase font-semibold text-white/50 mb-4">
                Experiences
              </h3>
              <div className="flex flex-wrap gap-2">
                {['Wine Tastings', 'Food & Pairings', 'Live Music', 'Markets & Pop-Ups', 'Lifestyle Events', 'Special Events'].map((tag) => (
                  <Link
                    key={tag}
                    to={`/events?category=${encodeURIComponent(tag)}`}
                    className="text-xs bg-white/10 hover:bg-white/20 transition-colors rounded-full px-3 py-1.5 text-white/70 hover:text-white"
                  >
                    {tag}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 mt-12 pt-8 space-y-4">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-white/40 text-xs">
              &copy; {new Date().getFullYear()} VinePass. All rights reserved.
            </p>
            <div className="flex items-center flex-wrap justify-center gap-x-5 gap-y-2">
              <Link to="/terms" className="text-white/40 hover:text-white/70 transition-colors text-xs">
                Terms &amp; Conditions
              </Link>
              <Link to="/privacy" className="text-white/40 hover:text-white/70 transition-colors text-xs">
                Privacy Policy
              </Link>
              <Link to="/refund-policy" className="text-white/40 hover:text-white/70 transition-colors text-xs">
                Refund Policy
              </Link>
              <Link to="/organiser-terms" className="text-white/40 hover:text-white/70 transition-colors text-xs">
                Organiser Terms
              </Link>
              <Link to="/payout-policy" className="text-white/40 hover:text-white/70 transition-colors text-xs">
                Payout Policy
              </Link>
              <Link to="/cookies" className="text-white/40 hover:text-white/70 transition-colors text-xs">
                Cookies
              </Link>
              <Link to="/about" className="text-white/40 hover:text-white/70 transition-colors text-xs">
                About
              </Link>
              <Link to="/fees" className="text-white/40 hover:text-white/70 transition-colors text-xs">
                Fees
              </Link>
              <Link to="/faq" className="text-white/40 hover:text-white/70 transition-colors text-xs" data-testid="footer-faq-bottom-link">
                FAQ
              </Link>
              <a href="mailto:info@vinepass.co.za" className="text-white/40 hover:text-white/70 transition-colors text-xs">
                Contact
              </a>
            </div>
          </div>
          {/* Registered company line — required by South African CIPC compliance */}
          <p
            className="text-white/35 text-[11px] tracking-wide text-center sm:text-right"
            data-testid="footer-company-registration"
          >
            {COMPANY_FOOTER_LINE}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
